// External libraries
import React from 'react';
import { css } from '@emotion/core';
import { useStoreState } from '@/store';
import {
  CardNumber as Number, // @ts-expect-error untyped-import
} from '@checkout/react-components';

// Internal modules
import {
  CROSS_BORDER_ERROR,
  getContent,
  getGlobals,
  getLocale,
  logger,
} from '@/lib';
import { getCardIconPosition } from './utils';
import { useMountLogging } from '@/lib/reactHooks';
import { logBlurOrFocusEvent as focusHandler } from '../../../Form/utils';
import { blurHandler, cardTypesChangeHandler, changeHandler } from './handlers';
import { LoadingElement } from '@/components/Common';

const CardNumber = (): JSX.Element => {
  useMountLogging('CardNumber');
  const allowedCardTypes = useStoreState(({ api }) => api.allowedCardTypes);
  const cardNumber = useStoreState(({ form }) => form.cardNumber);
  const hasCardTypeSelector = useStoreState(({ ui }) => ui.hasCardTypeSelector);
  const isFormSubmitting = useStoreState(({ ui }) => ui.isFormSubmitting);
  const { country: merchantCountry } = useStoreState(
    ({ api }) => api.cartDetails?.merchant ?? {},
  );
  const { deployEnv: env } = getGlobals();

  if (!allowedCardTypes.length) {
    return <LoadingElement styleOverrides={css({ marginBottom: '12px' })} />;
  }

  if (
    Boolean(merchantCountry) &&
    cardNumber.errorCode === CROSS_BORDER_ERROR.NON_DOMESTIC_CARD
  ) {
    logger.fpti('cross_border_card_message_shown', {
      buyer_country: getLocale().country,
      merchant_country: merchantCountry,
      state_name: 'cross_border_eligibility',
      transition_name: 'prepare_cross_border_unsupported_message_shown',
    });
  }

  return (
    <Number
      allowedCardTypes={allowedCardTypes}
      blurHandler={blurHandler}
      changeHandler={changeHandler}
      disabled={isFormSubmitting}
      displayCardArt={getCardIconPosition(cardNumber.cardType)}
      focusHandler={focusHandler}
      label={getContent('fieldLabels.cardNumber')}
      onAfterAllowedCardTypesChange={cardTypesChangeHandler}
      onAfterCardTypeChange={cardTypesChangeHandler}
      shouldAutoDetectCardType={!hasCardTypeSelector}
      luhnValidateJaws={!env.production}
      {...cardNumber}
    />
  );
};

export { CardNumber };
